import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from './AuthContext';
import { DEFAULT_COLOR_PALETTES, getColorPalette, generateCSSVariables } from '../config/colorPalettes';
import { loadPaletteCSS } from '../utils/cssLoader.js';

const ColorPaletteContext = createContext();

export const useColorPalette = () => {
    const context = useContext(ColorPaletteContext);
    if (!context) {
        throw new Error('useColorPalette must be used within a ColorPaletteProvider');
    }
    return context;
};

export const ColorPaletteProvider = ({ children }) => {
    const { currentUser } = useAuth();
    const [currentPalette, setCurrentPalette] = useState('default');
    const [defaultPalette, setDefaultPalette] = useState('default');
    const [customPalettes, setCustomPalettes] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);


    // Firestoreからカラーパレット設定を読み込み
    useEffect(() => {
        const loadColorPalette = async () => {
            if (!currentUser) {
                setCurrentPalette('default');
                setIsLoading(false);
                return;
            }

            try {
                const settingsDoc = await getDoc(doc(db, 'settings', currentUser.uid));
                if (settingsDoc.exists()) {
                    const settings = settingsDoc.data();

                    // カスタムパレットを読み込み
                    if (settings.customPalettes) {
                        setCustomPalettes(settings.customPalettes);
                    }

                    // デフォルトパレットを設定
                    if (settings.defaultPalette) {
                        if (DEFAULT_COLOR_PALETTES[settings.defaultPalette] ||
                            (settings.customPalettes && settings.customPalettes[settings.defaultPalette])) {
                            setDefaultPalette(settings.defaultPalette);
                        } else {
                            setDefaultPalette('default');
                        }
                    } else {
                        setDefaultPalette('default');
                    }

                    // 現在のパレットを設定
                    if (settings.colorPalette) {
                        // プリセットパレットまたはカスタムパレットかチェック
                        if (DEFAULT_COLOR_PALETTES[settings.colorPalette] ||
                            (settings.customPalettes && settings.customPalettes[settings.colorPalette])) {
                            setCurrentPalette(settings.colorPalette);
                        } else {
                            setCurrentPalette(settings.defaultPalette || 'default');
                        }
                    } else {
                        setCurrentPalette(settings.defaultPalette || 'default');
                    }
                } else {
                    setCurrentPalette('default');
                }
            } catch (err) {
                console.error('カラーパレット設定の読み込みに失敗しました:', err);
                setError('カラーパレット設定の読み込みに失敗しました');
                setCurrentPalette('default');
            } finally {
                setIsLoading(false);
            }
        };

        loadColorPalette();
    }, [currentUser]);

    // カラーパレットをFirestoreに保存
    const saveColorPalette = async (paletteKey) => {
        if (!currentUser) {
            throw new Error('ユーザーがログインしていません');
        }

        if (!DEFAULT_COLOR_PALETTES[paletteKey] && !customPalettes[paletteKey]) {
            throw new Error('無効なカラーパレットです');
        }

        try {
            setError(null);

            // 現在の設定を取得
            const settingsDoc = await getDoc(doc(db, 'settings', currentUser.uid));
            const currentSettings = settingsDoc.exists() ? settingsDoc.data() : {};

            // カラーパレット設定を更新
            const updatedSettings = {
                ...currentSettings,
                colorPalette: paletteKey,
                updatedAt: new Date()
            };

            await setDoc(doc(db, 'settings', currentUser.uid), updatedSettings);
            setCurrentPalette(paletteKey);

            return true;
        } catch (err) {
            console.error('カラーパレットの保存に失敗しました:', err);
            setError('カラーパレットの保存に失敗しました');
            throw err;
        }
    };

    // デフォルトパレットをFirestoreに保存
    const saveDefaultPalette = async (paletteKey) => {
        if (!currentUser) {
            throw new Error('ユーザーがログインしていません');
        }

        if (!DEFAULT_COLOR_PALETTES[paletteKey] && !customPalettes[paletteKey]) {
            throw new Error('無効なカラーパレットです');
        }

        try {
            setError(null);

            // 現在の設定を取得
            const settingsDoc = await getDoc(doc(db, 'settings', currentUser.uid));
            const currentSettings = settingsDoc.exists() ? settingsDoc.data() : {};

            // デフォルトパレット設定を更新
            const updatedSettings = {
                ...currentSettings,
                defaultPalette: paletteKey,
                updatedAt: new Date()
            };

            await setDoc(doc(db, 'settings', currentUser.uid), updatedSettings);
            setDefaultPalette(paletteKey);

            return true;
        } catch (err) {
            console.error('デフォルトパレットの保存に失敗しました:', err);
            setError('デフォルトパレットの保存に失敗しました');
            throw err;
        }
    };

    // 現在のカラーパレット情報を取得
    const getCurrentPaletteInfo = () => {
        // カスタムパレットかチェック
        if (customPalettes[currentPalette]) {
            return customPalettes[currentPalette];
        }
        return getColorPalette(currentPalette);
    };

    // デフォルトパレット情報を取得
    const getDefaultPaletteInfo = () => {
        // カスタムパレットかチェック
        if (customPalettes[defaultPalette]) {
            return customPalettes[defaultPalette];
        }
        return getColorPalette(defaultPalette);
    };

    // カスタムパレットを保存
    const saveCustomPalette = async (paletteData) => {
        if (!currentUser) {
            throw new Error('ユーザーがログインしていません');
        }

        try {
            setError(null);

            // パレットIDを生成（名前をベースにした安全なID）
            const paletteId = `custom_${Date.now()}_${paletteData.name.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase()}`;

            // 現在の設定を取得
            const settingsDoc = await getDoc(doc(db, 'settings', currentUser.uid));
            const currentSettings = settingsDoc.exists() ? settingsDoc.data() : {};

            // カスタムパレットを追加
            const updatedCustomPalettes = {
                ...currentSettings.customPalettes,
                [paletteId]: {
                    ...paletteData,
                    id: paletteId,
                    isCustom: true
                }
            };

            // 設定を更新
            const updatedSettings = {
                ...currentSettings,
                customPalettes: updatedCustomPalettes,
                updatedAt: new Date()
            };

            await setDoc(doc(db, 'settings', currentUser.uid), updatedSettings);
            setCustomPalettes(updatedCustomPalettes);

            return paletteId;
        } catch (err) {
            console.error('カスタムパレットの保存に失敗しました:', err);
            setError('カスタムパレットの保存に失敗しました');
            throw err;
        }
    };

    // カスタムパレットを更新
    const updateCustomPalette = async (paletteId, paletteData) => {
        if (!currentUser) {
            throw new Error('ユーザーがログインしていません');
        }

        if (!customPalettes[paletteId]) {
            throw new Error('指定されたカスタムパレットが見つかりません');
        }

        try {
            setError(null);

            // 現在の設定を取得
            const settingsDoc = await getDoc(doc(db, 'settings', currentUser.uid));
            const currentSettings = settingsDoc.exists() ? settingsDoc.data() : {};

            // カスタムパレットを更新
            const updatedCustomPalettes = {
                ...currentSettings.customPalettes,
                [paletteId]: {
                    ...paletteData,
                    id: paletteId,
                    isCustom: true,
                    updatedAt: new Date().toISOString()
                }
            };

            // 設定を更新
            const updatedSettings = {
                ...currentSettings,
                customPalettes: updatedCustomPalettes,
                updatedAt: new Date()
            };

            await setDoc(doc(db, 'settings', currentUser.uid), updatedSettings);
            setCustomPalettes(updatedCustomPalettes);

            return true;
        } catch (err) {
            console.error('カスタムパレットの更新に失敗しました:', err);
            setError('カスタムパレットの更新に失敗しました');
            throw err;
        }
    };

    // カスタムパレットを削除
    const deleteCustomPalette = async (paletteId) => {
        if (!currentUser) {
            throw new Error('ユーザーがログインしていません');
        }

        if (!customPalettes[paletteId]) {
            throw new Error('指定されたカスタムパレットが見つかりません');
        }

        try {
            setError(null);

            // 現在の設定を取得
            const settingsDoc = await getDoc(doc(db, 'settings', currentUser.uid));
            const currentSettings = settingsDoc.exists() ? settingsDoc.data() : {};

            // カスタムパレットを削除
            const updatedCustomPalettes = { ...currentSettings.customPalettes };
            delete updatedCustomPalettes[paletteId];

            // 削除されたパレットが現在選択中の場合、デフォルトに変更
            let newCurrentPalette = currentPalette;
            if (currentPalette === paletteId) {
                newCurrentPalette = 'default';
            }

            // 設定を更新
            const updatedSettings = {
                ...currentSettings,
                customPalettes: updatedCustomPalettes,
                colorPalette: newCurrentPalette,
                updatedAt: new Date()
            };

            await setDoc(doc(db, 'settings', currentUser.uid), updatedSettings);
            setCustomPalettes(updatedCustomPalettes);

            if (currentPalette === paletteId) {
                setCurrentPalette('default');
                applyCSSVariables('default');
            }

            return true;
        } catch (err) {
            console.error('カスタムパレットの削除に失敗しました:', err);
            setError('カスタムパレットの削除に失敗しました');
            throw err;
        }
    };

    // CSS変数を動的に適用
    const applyCSSVariables = async (paletteKey = currentPalette) => {
        try {
            await loadPaletteCSS(paletteKey, customPalettes);
        } catch (error) {
            console.warn('⚠️ カラーパレットの適用に失敗:', error);
        }
    };

    // カラーパレットが変更されたときにCSS変数を適用
    useEffect(() => {
        if (!isLoading) {
            applyCSSVariables(currentPalette).catch(error => {
                console.warn('⚠️ useEffect内でのカラーパレット適用に失敗:', error);
            });
        }
    }, [currentPalette, customPalettes, isLoading]);

    // パレット変更関数
    const changePalette = async (paletteKey) => {
        try {
            await saveColorPalette(paletteKey);
            applyCSSVariables(paletteKey);
        } catch (err) {
            // エラーは既にsetErrorで設定されている
            throw err;
        }
    };

    // 利用可能なパレット一覧を取得
    const getAvailablePalettes = () => {
        const presetPalettes = Object.entries(DEFAULT_COLOR_PALETTES).map(([key, palette]) => ({
            key,
            name: palette.name,
            colors: palette.colors,
            isCustom: false
        }));

        const customPaletteList = Object.entries(customPalettes).map(([key, palette]) => ({
            key,
            name: palette.name,
            colors: palette.colors,
            isCustom: true,
            createdAt: palette.createdAt,
            updatedAt: palette.updatedAt
        }));

        return [...presetPalettes, ...customPaletteList];
    };

    const value = {
        currentPalette,
        currentPaletteInfo: getCurrentPaletteInfo(),
        defaultPalette,
        defaultPaletteInfo: getDefaultPaletteInfo(),
        availablePalettes: getAvailablePalettes(),
        customPalettes,
        changePalette,
        saveColorPalette,
        saveDefaultPalette,
        saveCustomPalette,
        updateCustomPalette,
        deleteCustomPalette,
        applyCSSVariables,
        isLoading,
        error
    };

    return (
        <ColorPaletteContext.Provider value={value}>
            {children}
        </ColorPaletteContext.Provider>
    );
};

export default ColorPaletteProvider;
